/**
 * Pialu QW-1 - Mercado Pago cuotas display.
 *
 * Two responsibilities:
 *
 * 1. Recalculate the displayed cuota amount on Liquid-rendered blocks when:
 *    - A variant's price changes on PDP (MutationObserver on [data-price]).
 *    - The cart is updated via /cart/add, /cart/change, /cart/update, /cart/clear.
 *
 * 2. Dynamically INJECT the cuotas block into 3rd-party drawer apps that
 *    replace the native Shopify cart (e.g. UpCart at #CartPopup).
 *
 * Performance discipline:
 *  - No broad subtree observer on document.body (would fire on every app re-render).
 *  - One-shot body observer just to detect #CartPopup appearance.
 *  - Once attached, observe only #CartPopup, debounce 200ms, and disconnect
 *    while we mutate to avoid feedback loops.
 *  - /cart.js is fetched only on real cart events; otherwise we use the
 *    cached total. No fetch on each mutation.
 */
(function () {
  'use strict';

  var MAX_INSTALLMENTS = 6;
  var MIN_AMOUNT_CENTS = 500;
  var SYNC_THROTTLE_MS = 200;

  // -----------------------------------------------------------------
  // Cart reviews badge (style Trustpilot). Édite ces valeurs pour mettre
  // à jour la note affichée dans le drawer panier UpCart.
  // -----------------------------------------------------------------
  var REVIEWS_BADGE_CONFIG = {
    enabled: true,
    rating: 4.7,             // note sur 5
    count: 12322,            // nombre d'opiniones
    label: 'Excelente',      // texte gauche
    sourceLabel: 'opiniones',
    sourceUrl: ''            // si non vide, la source devient un lien
  };

  /* ----------------- shared helpers ----------------- */

  function parsePriceCents(text) {
    if (!text) return null;
    var stripped = String(text).replace(/[^0-9.,-]/g, '');
    if (!stripped) return null;
    var lastComma = stripped.lastIndexOf(',');
    var lastDot = stripped.lastIndexOf('.');
    var normalized = lastComma > lastDot
      ? stripped.replace(/\./g, '').replace(',', '.')
      : stripped.replace(/,/g, '');
    var n = parseFloat(normalized);
    return isNaN(n) ? null : Math.round(n * 100);
  }

  function formatMoney(cents) {
    return 'S/. ' + (cents / 100).toFixed(2);
  }

  // Update an existing block in place. Idempotent: bails out if value unchanged.
  function updateBlock(block, totalCents) {
    if (!block) return;
    if (totalCents == null || totalCents < MIN_AMOUNT_CENTS) {
      if (block.style.display !== 'none') block.style.display = 'none';
      return;
    }
    if (block.dataset.amountCents === String(totalCents) && block.style.display !== 'none') {
      return; // already at target, no-op
    }
    if (block.style.display === 'none') block.style.display = '';
    var max = parseInt(block.dataset.maxInstallments || String(MAX_INSTALLMENTS), 10);
    var amountEl = block.querySelector('[data-installments-mp-amount]');
    if (amountEl) {
      var perCuota = Math.floor(totalCents / max);
      var newText = formatMoney(perCuota);
      if (amountEl.textContent !== newText) amountEl.textContent = newText;
    }
    block.dataset.amountCents = String(totalCents);
  }

  /* ----------------- PDP variant price observer ----------------- */

  function findPdpBlockFor(priceEl) {
    var scope = priceEl.closest('[data-section-type="product"]') ||
                priceEl.closest('.product') ||
                priceEl.closest('form[action*="/cart/add"]') ||
                document;
    return scope.querySelector('[data-installments-mp][data-context="pdp"]');
  }

  function bindPriceObserver(priceEl) {
    if (!priceEl || priceEl.__installmentsBound) return;
    priceEl.__installmentsBound = true;
    var obs = new MutationObserver(function () {
      var cents = parsePriceCents(priceEl.textContent);
      if (cents == null) return;
      var pdp = findPdpBlockFor(priceEl);
      if (pdp) updateBlock(pdp, cents);
      var sticky = document.querySelector('[data-installments-mp][data-context="sticky"]');
      if (sticky) updateBlock(sticky, cents);
    });
    obs.observe(priceEl, { childList: true, characterData: true, subtree: true });
  }

  // Inject a cuotas block right after each price wrapper (main PDP + sticky).
  // Skips wrappers that already have a Liquid-rendered block, and skips
  // cross-sell / recommended-product cards.
  function injectPDP() {
    var wrappers = document.querySelectorAll('.product__price.ff-product-price');
    wrappers.forEach(function (wrap) {
      if (wrap.closest('.cross-sells, .recommended-products, .product-card, [data-cross-sell]')) return;
      var isSticky = wrap.classList.contains('sticky-atc-bar__price') || wrap.closest('.sticky-atc-bar');
      var context = isSticky ? 'sticky' : 'pdp';
      // The PDP and sticky-bar blocks are now rendered server-side (Liquid):
      // PDP just above the Add-to-Cart button, sticky inside the price column.
      // If one already exists anywhere on the page, never inject a duplicate.
      // JS still keeps both in sync on variant change via the [data-price]
      // observer + updateBlock().
      if ((context === 'pdp' || context === 'sticky') && document.querySelector('[data-installments-mp][data-context="' + context + '"]')) return;
      if (wrap.parentElement && wrap.parentElement.querySelector(':scope > [data-installments-mp][data-context="' + context + '"]')) return;
      var priceEl = wrap.querySelector('[data-price]');
      if (!priceEl) return;
      var cents = parsePriceCents(priceEl.textContent);
      if (cents == null || cents < MIN_AMOUNT_CENTS) return;
      var block = buildBlock(cents, context);
      wrap.insertAdjacentElement('afterend', block);
    });
  }

  function initPDP() {
    document.querySelectorAll('[data-price]').forEach(bindPriceObserver);
    injectPDP();
  }

  /* ----------------- UpCart / cart-page injection ----------------- */

  var latestCartCents = null;
  var cartFetchInFlight = false;
  var popupObserver = null;
  var bodyObserver = null;

  // Build a drawer / cart-page block from the merchant-editable global config
  // (window.PIALU_INSTALLMENTS_MP, injected by layout/theme.liquid). The PDP and
  // sticky blocks are rendered server-side by snippets/installments-mp.liquid.
  function buildBlock(amountCents, context) {
    var cfg = window.PIALU_INSTALLMENTS_MP || {};
    var count = parseInt(cfg.count, 10) || MAX_INSTALLMENTS;
    var div = document.createElement('div');
    div.className = 'installments-mp installments-mp--' + context;
    div.setAttribute('data-installments-mp', '');
    div.setAttribute('data-installments-mp-injected', '');
    div.setAttribute('data-context', context);
    div.setAttribute('data-max-installments', String(count));
    div.setAttribute('data-amount-cents', String(amountCents));
    if (cfg.fontSize) div.style.fontSize = parseFloat(cfg.fontSize) + 'px';
    if (cfg.color) div.style.color = cfg.color;
    if (cfg.fontWeight) div.style.fontWeight = String(cfg.fontWeight);

    var perCuota = Math.floor(amountCents / count);

    var iconHtml = '';
    if (cfg.showIcon !== false) {
      var sz = parseInt(cfg.iconSize, 10) || 16;
      if (cfg.iconSvg) {
        iconHtml = '<span class="installments-mp__icon" aria-hidden="true" style="width:' + sz + 'px;">' + cfg.iconSvg + '</span>';
      } else if (cfg.iconUrl) {
        iconHtml = '<span class="installments-mp__icon" aria-hidden="true" style="width:' + sz + 'px;"><img src="' + cfg.iconUrl + '" alt="" width="' + sz + '"></span>';
      } else {
        iconHtml =
          '<span class="installments-mp__icon" aria-hidden="true" style="width:' + sz + 'px;">' +
            '<svg width="' + sz + '" viewBox="0 0 16 12" fill="none" xmlns="http://www.w3.org/2000/svg">' +
              '<rect x="0.5" y="0.5" width="15" height="11" rx="1.5" stroke="currentColor"/>' +
              '<rect x="2" y="3" width="12" height="1.5" fill="currentColor"/>' +
              '<rect x="2" y="7" width="4" height="1" fill="currentColor" opacity="0.5"/>' +
            '</svg>' +
          '</span>';
      }
    }

    var tpl = cfg.textTemplate || 'Págalo en <strong>[n] cuotas sin intereses</strong> de [monto]/mes';
    var bodyText = tpl
      .replace(/\[n\]/g, String(count))
      .replace(/\[monto\]/g, '<span data-installments-mp-amount>' + formatMoney(perCuota) + '</span>');

    div.innerHTML = iconHtml + '<span class="installments-mp__text">' + bodyText + '</span>';
    return div;
  }

  function fetchCartCents(force) {
    if (cartFetchInFlight) return Promise.resolve(latestCartCents);
    if (!force && latestCartCents != null) return Promise.resolve(latestCartCents);
    if (!window.fetch) return Promise.resolve(null);
    cartFetchInFlight = true;
    return fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        cartFetchInFlight = false;
        if (!cart) return null;
        if (cart.currency && cart.currency !== 'PEN') { latestCartCents = null; return null; }
        latestCartCents = cart.total_price;
        return latestCartCents;
      })
      .catch(function () { cartFetchInFlight = false; return null; });
  }

  /* ----------------- reviews badge builder ----------------- */

  function buildStarSvg(type) {
    var GREEN = '#00b67a';
    var GRAY = '#dadada';
    var rectFill;
    if (type === 'full') {
      rectFill = '<rect width="20" height="20" rx="2" fill="' + GREEN + '"/>';
    } else if (type === 'half') {
      rectFill =
        '<rect x="0" y="0" width="10" height="20" fill="' + GREEN + '"/>' +
        '<rect x="10" y="0" width="10" height="20" fill="' + GRAY + '"/>';
    } else {
      rectFill = '<rect width="20" height="20" rx="2" fill="' + GRAY + '"/>';
    }
    var starPath = 'M10 3.8 12.06 8.1 16.7 8.6 13.2 11.95 14.16 16.5 10 14.2 5.84 16.5 6.8 11.95 3.3 8.6 7.94 8.1Z';
    return (
      '<svg class="cart-reviews-badge__star" viewBox="0 0 20 20" aria-hidden="true">' +
        rectFill +
        '<path d="' + starPath + '" fill="#ffffff"/>' +
      '</svg>'
    );
  }

  function buildReviewsBadge(cfg) {
    var rating = Math.max(0, Math.min(5, parseFloat(cfg.rating) || 0));
    var fullStars = Math.floor(rating);
    var frac = rating - fullStars;
    var hasHalf = frac >= 0.25 && frac < 0.75;
    if (frac >= 0.75) fullStars += 1;

    var stars = '';
    for (var i = 0; i < 5; i++) {
      var type;
      if (i < fullStars) type = 'full';
      else if (i === fullStars && hasHalf) type = 'half';
      else type = 'empty';
      stars += buildStarSvg(type);
    }

    var formattedCount;
    try {
      formattedCount = (cfg.count || 0).toLocaleString('es-PE');
    } catch (e) {
      formattedCount = String(cfg.count || 0);
    }

    var sourceHtml = cfg.sourceUrl
      ? '<a class="cart-reviews-badge__source" href="' + cfg.sourceUrl + '" target="_blank" rel="noopener">' + cfg.sourceLabel + '</a>'
      : '<span class="cart-reviews-badge__source">' + cfg.sourceLabel + '</span>';

    var div = document.createElement('div');
    div.className = 'cart-reviews-badge';
    div.setAttribute('data-cart-reviews-badge-injected', '');
    div.innerHTML =
      '<span class="cart-reviews-badge__label">' + cfg.label + '</span>' +
      '<span class="cart-reviews-badge__stars">' + stars + '</span>' +
      '<span class="cart-reviews-badge__count"><strong>' + rating.toFixed(1) + '/5</strong> · ' + formattedCount + ' ' + cfg.sourceLabel + '</span>';
    return div;
  }

  function injectReviewsBadge() {
    if (!REVIEWS_BADGE_CONFIG.enabled) return;
    var popup = document.getElementById('CartPopup');
    if (!popup) return;
    var footer = popup.querySelector('.upcart-footer');
    if (!footer) return;
    if (footer.querySelector('[data-cart-reviews-badge-injected]')) return;
    withObserverPaused(function () {
      var badge = buildReviewsBadge(REVIEWS_BADGE_CONFIG);
      // Anchor reviews badge just BEFORE the cuotas mention so it sits
      // directly above it (which itself sits just above the checkout button).
      var cuotas = footer.querySelector('[data-installments-mp][data-context="drawer"]');
      var checkoutBox = footer.querySelector('.upcart-checkout-button-container');
      if (cuotas && cuotas.parentElement) {
        cuotas.parentElement.insertBefore(badge, cuotas);
      } else if (checkoutBox && checkoutBox.parentElement) {
        checkoutBox.parentElement.insertBefore(badge, checkoutBox);
      } else {
        footer.appendChild(badge);
      }
    });
  }

  // Mutate the DOM with the observer paused so we don't trigger ourselves.
  function withObserverPaused(fn) {
    if (popupObserver) popupObserver.disconnect();
    try { fn(); } finally {
      var popup = document.getElementById('CartPopup');
      if (popupObserver && popup) {
        popupObserver.observe(popup, { childList: true, subtree: true });
      }
    }
  }

  function injectUpCart(cents) {
    if (window.PIALU_INSTALLMENTS_MP && window.PIALU_INSTALLMENTS_MP.showDrawer === false) return;
    var popup = document.getElementById('CartPopup');
    if (!popup) return;
    var footer = popup.querySelector('.upcart-footer');
    if (!footer) return;
    if (cents == null || cents < MIN_AMOUNT_CENTS) return;
    var existing = footer.querySelector('[data-installments-mp-injected]');
    if (existing) {
      updateBlock(existing, cents);
      return;
    }
    withObserverPaused(function () {
      var block = buildBlock(cents, 'drawer');
      // Order in drawer footer (top-down): ... > reviews badge > cuotas > checkout.
      // Anchor cuotas just BEFORE the checkout button container.
      var checkoutBox = footer.querySelector('.upcart-checkout-button-container');
      if (checkoutBox && checkoutBox.parentElement) {
        checkoutBox.parentElement.insertBefore(block, checkoutBox);
      } else {
        footer.appendChild(block);
      }
    });
  }

  function injectCartPage(cents) {
    if (window.PIALU_INSTALLMENTS_MP && window.PIALU_INSTALLMENTS_MP.showDrawer === false) return;
    if (location.pathname.indexOf('/cart') !== 0) return;
    if (document.querySelector('[data-installments-mp-injected][data-context="cart"]')) return;
    if (cents == null || cents < MIN_AMOUNT_CENTS) return;
    var anchor = document.querySelector('.cart__footer-subtotal') ||
                 document.querySelector('.cart__footer') ||
                 document.querySelector('.cart__input-buttons');
    if (!anchor) return;
    var block = buildBlock(cents, 'cart');
    anchor.insertAdjacentElement('afterend', block);
  }

  /* ----------------- throttled sync ----------------- */

  var pendingSync = null;
  var lastSyncAt = 0;

  function debouncedSync() {
    var now = Date.now();
    var delay = Math.max(0, SYNC_THROTTLE_MS - (now - lastSyncAt));
    if (pendingSync) return; // a sync is already queued
    pendingSync = setTimeout(function () {
      pendingSync = null;
      lastSyncAt = Date.now();
      fetchCartCents(false).then(function (cents) {
        injectUpCart(cents);
        injectCartPage(cents);
        injectReviewsBadge();
      });
    }, delay);
  }

  function refreshOnCartEvent() {
    // Real cart change: force re-fetch.
    fetchCartCents(true).then(function (cents) {
      document.querySelectorAll(
        '[data-installments-mp][data-context="drawer"], [data-installments-mp][data-context="cart"]'
      ).forEach(function (block) { updateBlock(block, cents); });
      injectUpCart(cents);
      injectCartPage(cents);
      injectReviewsBadge();
    });
  }

  /* ----------------- observers ----------------- */

  function attachPopupObserver() {
    var popup = document.getElementById('CartPopup');
    if (!popup) return false;
    if (popup.__installmentsAttached) return true;
    popup.__installmentsAttached = true;
    popupObserver = new MutationObserver(function () { debouncedSync(); });
    popupObserver.observe(popup, { childList: true, subtree: true });
    debouncedSync();
    return true;
  }

  function watchForPopupAppearance() {
    if (attachPopupObserver()) return;
    // One-shot body observer: only listen for the moment #CartPopup is added.
    bodyObserver = new MutationObserver(function () {
      if (document.getElementById('CartPopup')) {
        if (bodyObserver) bodyObserver.disconnect();
        bodyObserver = null;
        attachPopupObserver();
      }
    });
    bodyObserver.observe(document.body, { childList: true, subtree: true });
  }

  /* ----------------- cart event hooks ----------------- */

  ['cart:updated', 'cart:item-added', 'cart:refresh', 'cart:change'].forEach(function (ev) {
    document.addEventListener(ev, refreshOnCartEvent);
  });

  if (window.fetch) {
    var originalFetch = window.fetch;
    window.fetch = function (input) {
      var url = '';
      if (typeof input === 'string') url = input;
      else if (input && input.url) url = input.url;
      var promise = originalFetch.apply(this, arguments);
      if (url && /\/cart\/(add|change|update|clear)/.test(url)) {
        promise.then(function () { setTimeout(refreshOnCartEvent, 120); }).catch(function () {});
      }
      return promise;
    };
  }

  if (window.XMLHttpRequest) {
    var XHRopen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url) {
      this.__installmentsMpUrl = url;
      return XHRopen.apply(this, arguments);
    };
    var XHRsend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.send = function () {
      if (this.__installmentsMpUrl && /\/cart\/(add|change|update|clear)/.test(this.__installmentsMpUrl)) {
        this.addEventListener('load', function () { setTimeout(refreshOnCartEvent, 120); });
      }
      return XHRsend.apply(this, arguments);
    };
  }

  /* ----------------- boot ----------------- */

  function boot() {
    initPDP();
    watchForPopupAppearance();
    // Prime the cache so the first popup open has the value ready.
    fetchCartCents(true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', initPDP);
  document.addEventListener('shopify:section:select', initPDP);
})();
